import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
	ChevronDown,
	ExternalLink,
	Loader2,
	Paperclip,
	PenLine,
	Plus,
	Trash2,
	Upload,
} from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { IntervalLocation } from "@/features/presence/components/interval-location";
import { useDeleteAttachment } from "@/features/presence/hooks/use-delete-attachment";
import { useHolidayNames } from "@/features/presence/hooks/use-holidays";
import { useNow } from "@/features/presence/hooks/use-now";
import { useOpenAttachment } from "@/features/presence/hooks/use-open-attachment";
import { usePutDay } from "@/features/presence/hooks/use-put-day";
import { useUploadAttachment } from "@/features/presence/hooks/use-upload-attachment";
import type {
	AttendanceAttachment,
	AttendanceDay,
	TimeEntry,
	TimeEntryWorker,
} from "@/features/presence/types";
import {
	dayKindLabel,
	RECORD_STATUSES,
	requiresJustification,
	STATUS_LABELS,
} from "@/features/presence/utils/attendance";
import { hasOpenEntry, sortIntervals } from "@/features/presence/utils/day";
import {
	entryMinutes,
	formatClock,
	formatMinutes,
	formatPercent,
	formatSignedMinutes,
	timeEntryErrorMessage,
} from "@/features/presence/utils/format";
import { dateOnlyToDate } from "@/lib/format";
import {
	type DayFormValues,
	daySchema,
	formatOptionalNumber,
	parseOptionalNumber,
} from "../../schemas/day-schema";
import { TimeEntryDialog } from "../time-entry-dialog";

const ACCEPTED_FILES =
	"application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";
const JUSTIFICATION_HINT =
	"Alterações em dias anteriores exigem justificativa.";

function fullDateLabel(date: string): string {
	const parsed = dateOnlyToDate(date);
	if (!parsed) return date;
	const label = format(parsed, "EEEE, d 'de' MMMM 'de' yyyy", {
		locale: ptBR,
	});
	return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DayEditor({
	open,
	onOpenChange,
	worker,
	day,
	canUpdate,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	worker: TimeEntryWorker | undefined;
	day: AttendanceDay | undefined;
	canUpdate: boolean;
}) {
	const putDay = usePutDay();
	const upload = useUploadAttachment();
	const remove = useDeleteAttachment();
	const openAttachment = useOpenAttachment();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [entryDialogOpen, setEntryDialogOpen] = useState(false);
	const [entryMode, setEntryMode] = useState<"create" | "edit">("create");
	const [editingEntry, setEditingEntry] = useState<TimeEntry | undefined>();
	const [extrasOpen, setExtrasOpen] = useState(false);

	const entries = sortIntervals(day?.entries ?? []);
	const now = useNow(hasOpenEntry(entries));
	const year = day ? dateOnlyToDate(day.date)?.getFullYear() : undefined;
	const holidays = useHolidayNames(year, open && day?.kind === "holiday");
	const holidayName = day ? holidays.data?.get(day.date) : undefined;
	const pastDay = day ? requiresJustification(day.date) : false;
	const attachments = day?.record?.attachments ?? [];
	const readOnly = !canUpdate;

	const {
		register,
		handleSubmit,
		control,
		reset,
		setError,
		formState: { errors },
	} = useForm<DayFormValues>({
		resolver: zodResolver(daySchema),
		defaultValues: {
			status: "present",
			note: "",
			overtimeMinutesOverride: "",
			overtimePctOverride: "",
			editNote: "",
			requiresJustification: false,
		},
	});

	const dayKey = day ? `${worker?.id}:${day.date}` : "";
	const lastResetKey = useRef("");
	useEffect(() => {
		if (!open) {
			lastResetKey.current = "";
			return;
		}
		if (lastResetKey.current === dayKey) return;
		lastResetKey.current = dayKey;
		reset({
			status: day?.record?.status ?? "present",
			note: day?.record?.note ?? "",
			overtimeMinutesOverride: formatOptionalNumber(
				day?.record?.overtimeMinutesOverride ?? null,
			),
			overtimePctOverride: formatOptionalNumber(
				day?.record?.overtimePctOverride ?? null,
			),
			editNote: "",
			requiresJustification: pastDay,
		});
		setExtrasOpen(
			day?.record?.overtimeMinutesOverride != null ||
				day?.record?.overtimePctOverride != null,
		);
	}, [open, dayKey, day, pastDay, reset]);

	function openCreateEntry() {
		setEntryMode("create");
		setEditingEntry(undefined);
		setEntryDialogOpen(true);
	}

	function openEditEntry(entry: TimeEntry) {
		setEntryMode("edit");
		setEditingEntry(entry);
		setEntryDialogOpen(true);
	}

	async function onSubmit(values: DayFormValues) {
		if (!worker || !day) return;
		try {
			await putDay.mutateAsync({
				workerId: worker.id,
				date: day.date,
				status: values.status,
				note: values.note,
				overtimeMinutesOverride: parseOptionalNumber(
					values.overtimeMinutesOverride,
				),
				overtimePctOverride: parseOptionalNumber(values.overtimePctOverride),
				editNote: values.editNote,
			});
			toast.success("Dia salvo");
			onOpenChange(false);
		} catch (err) {
			const message = timeEntryErrorMessage(
				err,
				"Não foi possível salvar o dia.",
			);
			if (message === JUSTIFICATION_HINT) {
				setError("editNote", {
					message: "Descreva a justificativa da alteração",
				});
			}
			toast.error(message);
		}
	}

	async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file || !worker || !day) return;
		try {
			await upload.mutateAsync({ workerId: worker.id, date: day.date, file });
			toast.success("Anexo enviado");
		} catch (err) {
			toast.error(
				timeEntryErrorMessage(err, "Não foi possível enviar o anexo."),
			);
		}
	}

	async function onRemoveAttachment(attachment: AttendanceAttachment) {
		if (!worker || !day) return;
		try {
			await remove.mutateAsync({
				workerId: worker.id,
				date: day.date,
				attachmentId: attachment.id,
			});
			toast.success("Anexo removido");
		} catch (err) {
			toast.error(
				timeEntryErrorMessage(err, "Não foi possível remover o anexo."),
			);
		}
	}

	async function onOpenAttachment(attachment: AttendanceAttachment) {
		if (!worker || !day) return;
		try {
			await openAttachment.mutateAsync({
				workerId: worker.id,
				date: day.date,
				attachmentId: attachment.id,
			});
		} catch (err) {
			toast.error(
				timeEntryErrorMessage(err, "Não foi possível abrir o anexo."),
			);
		}
	}

	const busy = putDay.isPending || upload.isPending || remove.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="day-editor">
				<DialogHeader>
					<DialogTitle>{worker?.name ?? "Dia"}</DialogTitle>
					<DialogDescription className="day-editor__subtitle">
						{day && (
							<>
								<span className="day-editor__date">
									{fullDateLabel(day.date)}
								</span>
								<Badge variant="outline" data-kind={day.kind}>
									{dayKindLabel(day.kind, holidayName)}
								</Badge>
								<Badge variant="secondary" data-status={day.status}>
									{STATUS_LABELS[day.status]}
								</Badge>
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				{day && worker && (
					<div className="day-editor__body">
						<section className="day-editor__section">
							<header className="day-editor__section-header">
								<h3>Intervalos</h3>
								{canUpdate && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={openCreateEntry}
									>
										<Plus />
										Adicionar intervalo
									</Button>
								)}
							</header>
							{entries.length === 0 ? (
								<p className="day-editor__empty">
									Nenhum intervalo registrado.
								</p>
							) : (
								<ul className="day-editor__intervals">
									{entries.map((entry) => (
										<li key={entry.id} data-entry-id={entry.id}>
											<span className="day-editor__interval-times">
												{formatClock(entry.clockedInAt)}
												<span aria-hidden="true">→</span>
												{entry.clockedOutAt
													? formatClock(entry.clockedOutAt)
													: "em curso"}
											</span>
											<IntervalLocation entry={entry} modal />
											<span className="day-editor__interval-total">
												{formatMinutes(entryMinutes(entry, now))}
											</span>
											{entry.edited && (
												<Tooltip>
													<TooltipTrigger asChild>
														<Badge variant="outline">Ajustado</Badge>
													</TooltipTrigger>
													<TooltipContent>
														{entry.edited.note || "Registro ajustado"}
													</TooltipContent>
												</Tooltip>
											)}
											{canUpdate && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													aria-label={`Corrigir intervalo das ${formatClock(entry.clockedInAt)}`}
													onClick={() => openEditEntry(entry)}
												>
													<PenLine />
													Corrigir
												</Button>
											)}
										</li>
									))}
								</ul>
							)}
						</section>

						<dl className="day-editor__totals">
							<div>
								<dt>Trabalhado</dt>
								<dd data-field="worked">
									{formatMinutes(day.totals.workedMinutes)}
								</dd>
							</div>
							<div>
								<dt>Previsto</dt>
								<dd data-field="expected">
									{formatMinutes(day.totals.expectedMinutes)}
								</dd>
							</div>
							<div>
								<dt>Extras</dt>
								<dd data-field="overtime">
									{formatSignedMinutes(day.totals.overtimeMinutes)}
								</dd>
							</div>
							<div>
								<dt>Equivalente ({formatPercent(day.totals.overtimePct)})</dt>
								<dd data-field="equivalent">
									{formatSignedMinutes(day.totals.overtimeEquivalentMinutes)}
								</dd>
							</div>
						</dl>

						<form
							id="day-editor-form"
							className="day-editor__form"
							onSubmit={handleSubmit(onSubmit)}
							noValidate
						>
							<div className="form-group">
								<Label htmlFor="day-editor-status">Status do dia</Label>
								<Controller
									control={control}
									name="status"
									render={({ field }) => (
										<Select
											value={field.value}
											onValueChange={field.onChange}
											disabled={readOnly}
										>
											<SelectTrigger
												id="day-editor-status"
												className="day-editor__select"
												aria-invalid={!!errors.status}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{RECORD_STATUSES.map((status) => (
													<SelectItem key={status} value={status}>
														{STATUS_LABELS[status]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</div>

							<div className="form-group">
								<Label htmlFor="day-editor-note">Observação</Label>
								<Textarea
									id="day-editor-note"
									placeholder="Ex.: saiu mais cedo para consulta"
									aria-invalid={!!errors.note}
									disabled={readOnly}
									{...register("note")}
								/>
								{errors.note && (
									<p className="form-error">{errors.note.message}</p>
								)}
							</div>

							<Collapsible
								open={extrasOpen}
								onOpenChange={setExtrasOpen}
								className="day-editor__extras"
							>
								<CollapsibleTrigger asChild>
									<button type="button" className="day-editor__extras-trigger">
										<span>Ajustar extras</span>
										<ChevronDown className="day-editor__extras-chevron" />
									</button>
								</CollapsibleTrigger>
								<CollapsibleContent className="day-editor__extras-fields">
									<div className="form-group">
										<Label htmlFor="day-editor-overtime-minutes">
											Extras (minutos)
										</Label>
										<Input
											id="day-editor-overtime-minutes"
											inputMode="numeric"
											placeholder={String(day.totals.overtimeMinutes)}
											aria-invalid={!!errors.overtimeMinutesOverride}
											disabled={readOnly}
											{...register("overtimeMinutesOverride")}
										/>
										{errors.overtimeMinutesOverride && (
											<p className="form-error">
												{errors.overtimeMinutesOverride.message}
											</p>
										)}
									</div>
									<div className="form-group">
										<Label htmlFor="day-editor-overtime-pct">
											Percentual (%)
										</Label>
										<Input
											id="day-editor-overtime-pct"
											inputMode="decimal"
											placeholder={String(day.totals.overtimePct)}
											aria-invalid={!!errors.overtimePctOverride}
											disabled={readOnly}
											{...register("overtimePctOverride")}
										/>
										{errors.overtimePctOverride && (
											<p className="form-error">
												{errors.overtimePctOverride.message}
											</p>
										)}
									</div>
									<p className="form-hint">
										Deixe em branco para usar o cálculo da jornada.
									</p>
								</CollapsibleContent>
							</Collapsible>

							{pastDay && canUpdate && (
								<div className="form-group">
									<Label htmlFor="day-editor-edit-note">Justificativa</Label>
									<Textarea
										id="day-editor-edit-note"
										placeholder="Ex.: atestado entregue no dia seguinte"
										aria-invalid={!!errors.editNote}
										{...register("editNote")}
									/>
									<p className="form-hint">{JUSTIFICATION_HINT}</p>
									{errors.editNote && (
										<p className="form-error">{errors.editNote.message}</p>
									)}
								</div>
							)}
						</form>

						<section className="day-editor__section">
							<header className="day-editor__section-header">
								<h3>Anexos</h3>
								{canUpdate && (
									<>
										<input
											ref={fileInputRef}
											type="file"
											accept={ACCEPTED_FILES}
											aria-label="Arquivo do anexo"
											className="day-editor__file-input"
											onChange={onFileChange}
										/>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={upload.isPending}
											onClick={() => fileInputRef.current?.click()}
										>
											{upload.isPending ? (
												<Loader2 className="day-editor__spinner" />
											) : (
												<Upload />
											)}
											Anexar arquivo
										</Button>
									</>
								)}
							</header>
							{attachments.length === 0 ? (
								<p className="day-editor__empty">
									Nenhum anexo. Aceita PDF, JPG ou PNG.
								</p>
							) : (
								<ul className="day-editor__attachments">
									{attachments.map((attachment) => (
										<li key={attachment.id} data-attachment-id={attachment.id}>
											<Paperclip className="day-editor__attachment-icon" />
											<span className="day-editor__attachment-name">
												{attachment.fileName}
											</span>
											<span className="day-editor__attachment-size">
												{formatSize(attachment.sizeBytes)}
											</span>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												aria-label={`Abrir ${attachment.fileName}`}
												disabled={openAttachment.isPending}
												onClick={() => onOpenAttachment(attachment)}
											>
												<ExternalLink />
												Abrir
											</Button>
											{canUpdate && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													aria-label={`Remover ${attachment.fileName}`}
													disabled={remove.isPending}
													onClick={() => onRemoveAttachment(attachment)}
												>
													<Trash2 />
													Remover
												</Button>
											)}
										</li>
									))}
								</ul>
							)}
						</section>
					</div>
				)}

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={putDay.isPending}
					>
						{canUpdate ? "Cancelar" : "Fechar"}
					</Button>
					{canUpdate && (
						<Button type="submit" form="day-editor-form" disabled={busy}>
							{putDay.isPending && <Loader2 className="day-editor__spinner" />}
							Salvar dia
						</Button>
					)}
				</DialogFooter>

				{worker && day && (
					<TimeEntryDialog
						open={entryDialogOpen}
						onOpenChange={setEntryDialogOpen}
						mode={entryMode}
						entry={editingEntry}
						workers={[]}
						defaults={{
							workerId: worker.id,
							workerName: worker.name,
							workDate: day.date,
						}}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}
